import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'
import { Dialog } from './dialog'

interface ConfirmOptions {
  title: string
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  dangerous?: boolean
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

interface ConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmState | null>(null)

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...options, resolve })
      }),
    [],
  )

  const handle = (value: boolean) => {
    pending?.resolve(value)
    setPending(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <Dialog
          open
          onOpenChange={(o) => !o && handle(false)}
          size="sm"
          title={pending.title}
          primaryLabel={pending.confirmLabel ?? 'Confirm'}
          primaryVariant={pending.dangerous ? 'danger' : 'primary'}
          onPrimary={() => handle(true)}
          secondaryLabel={pending.cancelLabel ?? 'Cancel'}
          onSecondary={() => handle(false)}
        >
          {pending.message && (
            <p className="text-sm text-text-secondary">{pending.message}</p>
          )}
        </Dialog>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext)
  if (!fn) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>')
  }
  return fn
}
